import { useState } from "react";
import axios from "axios";
import { Sparkles, Check, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function JourneyParser({ onApply }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  const analyze = async () => {
    setError("");
    if (!text.trim()) {
      setError("Add a short description of the journey first.");
      return;
    }
    setBusy(true);
    try {
      const response = await axios.post(`${API}/ai/parse-journey`, { text });
      setPreview(response.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Sorry — the AI helper is unavailable.");
    } finally {
      setBusy(false);
    }
  };

  const use = () => {
    if (preview && onApply) onApply(preview);
    setPreview(null);
    setText("");
  };

  const dismiss = () => setPreview(null);

  return (
    <div className="journey-parser" data-testid="journey-parser">
      <div className="jp-head">
        <Sparkles size={16} />
        <span className="eyebrow">AI helper</span>
        <small>Describe your trip in your own words</small>
      </div>
      <div className="jp-input">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder='e.g. "Pick me up at Colchester North station tomorrow at 8:30am, 2 people going to the hospital."'
          rows={2}
          disabled={busy}
          data-testid="journey-parser-input"
        />
        <button
          type="button"
          onClick={analyze}
          disabled={busy || !text.trim()}
          className="secondary-button"
          data-testid="journey-parser-analyze-button"
        >
          {busy ? "Reading…" : "Read my trip"}
        </button>
      </div>
      {error && (
        <p className="jp-error" data-testid="journey-parser-error">{error}</p>
      )}
      {preview && (
        <div className="jp-preview" data-testid="journey-parser-preview">
          <p className="jp-summary" data-testid="journey-parser-summary">{preview.summary || "Here's what I understood:"}</p>
          <dl>
            <div><dt>Pickup</dt><dd data-testid="jp-preview-pickup">{preview.pickup || "—"}</dd></div>
            <div><dt>Destination</dt><dd data-testid="jp-preview-destination">{preview.destination || "—"}</dd></div>
            <div><dt>Date</dt><dd data-testid="jp-preview-date">{preview.date || "—"}</dd></div>
            <div><dt>Time</dt><dd data-testid="jp-preview-time">{preview.time || "—"}</dd></div>
            <div><dt>Passengers</dt><dd data-testid="jp-preview-passengers">{preview.passengers}</dd></div>
            {preview.notes && <div><dt>Notes</dt><dd data-testid="jp-preview-notes">{preview.notes}</dd></div>}
          </dl>
          <div className="jp-actions">
            <button type="button" className="primary-button" onClick={use} data-testid="journey-parser-apply-button">
              <Check size={16} /> Use these details
            </button>
            <button type="button" className="text-link" onClick={dismiss} data-testid="journey-parser-dismiss-button">
              <X size={14} /> Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
